import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/authContext.jsx';
import { useAlertasObras } from '../hooks/useAlertasObras.js';
import { useAbrirObra } from '../hooks/useAbrirObra.js';
import { Modal } from './ui/Modal.jsx';

// Burbuja flotante de alertas de Obras (migración 085), con el mismo formato
// que la de Mejoramientos: botón redondo con campana arriba a la derecha,
// panel con tarjetas y la acción según el ciclo dentro de cada tarjeta.
const ETIQUETA_ACCION = (ciclo) => (Number(ciclo) >= 3 ? 'Confeccionar PV' : 'Enviar notificación');

const fechaDia = (dia) => {
    if (!dia) return '—';
    const [y, m, d] = String(dia).slice(0, 10).split('-');
    return y && m && d ? `${d}/${m}/${y}` : '—';
};
const transcurridos = (n) => (n == null ? '—' : `${n} días`);
const restantes = (n) => {
    if (n == null) return '—';
    if (n > 0) return `faltan ${n} día${n === 1 ? '' : 's'}`;
    if (n === 0) return 'vence hoy';
    return `vencido hace ${-n} día${-n === 1 ? '' : 's'}`;
};

const BORDE_CICLO = {
    amarillo: 'border-l-4 border-l-amber-400',
    naranja: 'border-l-4 border-l-orange-400',
    rojo: 'border-l-4 border-l-red-500',
    violeta: 'border-l-4 border-l-violet-500',
};
const BADGE_CICLO = {
    amarillo: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    naranja: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    rojo: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    violeta: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
};
const CIRCULO_CICLO = {
    amarillo: 'bg-amber-500',
    naranja: 'bg-orange-500',
    rojo: 'bg-red-600',
    violeta: 'bg-violet-600',
};

const AlertasObrasBurbuja = () => {
    const { token } = useContext(AuthContext);
    const [abierto, setAbierto] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const { pendientes, cargando, puede, recargar } = useAlertasObras();
    const navigate = useNavigate();
    const { abrirObra } = useAbrirObra('alertas_obras');
    const clickTimer = useRef(null);

    const [headerOffset, setHeaderOffset] = useState(84);
    useEffect(() => {
        const medir = () => {
            const h = document.querySelector('header');
            if (h) setHeaderOffset(Math.ceil(h.getBoundingClientRect().height) + 12);
        };
        medir();
        window.addEventListener('resize', medir);
        return () => window.removeEventListener('resize', medir);
    }, []);

    // Modal de acción
    const [accion, setAccion] = useState(null); // { item, esPv }
    const [draft, setDraft] = useState(null);
    const [cargandoDraft, setCargandoDraft] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [mensaje, setMensaje] = useState('');

    // Un click filtra la página por la obra; doble click abre la obra para editar.
    const handleClickObra = (codigo) => {
        if (clickTimer.current) clearTimeout(clickTimer.current);
        clickTimer.current = setTimeout(() => {
            clickTimer.current = null;
            setAbierto(false);
            navigate(`/alertas-obras?obra=${encodeURIComponent(codigo)}`);
        }, 250);
    };
    const handleDobleClickObra = (codigo) => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        setAbierto(false);
        abrirObra(codigo);
    };

    const abrirAccion = async (item) => {
        const esPv = Number(item.ciclo) >= 3;
        setAccion({ item, esPv });
        setDraft(null);
        setMensaje('');
        setCargandoDraft(true);
        try {
            const res = await fetch(`/api/alertas-obras/${item.id}/notificacion`, {
                headers: { Authorization: `Bearer ${token}` },
                cache: 'no-store',
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json?.message || 'No se pudo preparar el mail');
            setDraft({
                destinatarios: (json.data.destinatarios || []).join(', '),
                asunto: json.data.asunto || '',
                cuerpo: json.data.cuerpo || '',
                descripcion: '',
                nroPv: '',
            });
        } catch (e) {
            setMensaje(e.message);
        } finally {
            setCargandoDraft(false);
        }
    };

    const cerrarAccion = () => {
        setAccion(null);
        setDraft(null);
        setMensaje('');
    };

    const enviarAccion = async () => {
        if (!accion || !draft) return;
        setEnviando(true);
        setMensaje('');
        try {
            const { item, esPv } = accion;
            const url = esPv
                ? `/api/alertas-obras/${item.id}/resolver`
                : `/api/alertas-obras/${item.id}/notificacion`;
            const body = esPv
                ? {
                    nroPv: draft.nroPv,
                    descripcion: draft.descripcion,
                    email: {
                        destinatarios: draft.destinatarios,
                        asunto: draft.asunto,
                        cuerpo: draft.cuerpo,
                    },
                }
                : {
                    destinatarios: draft.destinatarios,
                    asunto: draft.asunto,
                    cuerpo: draft.cuerpo,
                    descripcion: draft.descripcion,
                };
            const res = await fetch(url, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json?.message || 'No se pudo enviar');
            cerrarAccion();
            await recargar();
        } catch (e) {
            setMensaje(e.message);
        } finally {
            setEnviando(false);
        }
    };

    // Igual que Mejoramientos: sin pendientes no se muestra la burbuja.
    if (!puede || pendientes.length === 0) return null;

    const termino = busqueda.trim().toLowerCase();
    const filtradas = termino
        ? pendientes.filter((p) =>
            String(p.codigoObra || '').toLowerCase().includes(termino)
            || String(p.nombreObra || '').toLowerCase().includes(termino))
        : pendientes;

    // Cantidad por ciclo para mostrar en la campanita (sobre el total).
    const conteosCiclo = (() => {
        const m = new Map();
        for (const p of pendientes) {
            const c = Number(p.ciclo);
            if (!m.has(c)) m.set(c, { ciclo: c, count: 0, color: p.color });
            m.get(c).count += 1;
        }
        return [...m.values()].sort((a, b) => a.ciclo - b.ciclo);
    })();

    // Agrupadas por ciclo, con la cantidad de cada uno.
    const grupos = (() => {
        const m = new Map();
        for (const p of filtradas) {
            const c = Number(p.ciclo);
            if (!m.has(c)) m.set(c, []);
            m.get(c).push(p);
        }
        return [...m.entries()].sort((a, b) => a[0] - b[0]);
    })();

    return (
        <>
            <div style={{ top: headerOffset }} className="fixed right-4 z-50">
                <button
                    type="button"
                    onClick={() => setAbierto((v) => !v)}
                    title="Alertas de Obras (anticipo sin acta)"
                    className="w-14 h-14 rounded-full bg-teal-600 text-white shadow-lg hover:bg-teal-700 flex items-center justify-center"
                >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                </button>
                {conteosCiclo.length > 0 && (
                    <div className="absolute -top-2 -right-2 z-10 flex flex-col items-center gap-0.5">
                        {conteosCiclo.map((c) => (
                            <span
                                key={c.ciclo}
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow ${CIRCULO_CICLO[c.color] || CIRCULO_CICLO.violeta}`}
                                title={`Ciclo ${c.ciclo}: ${c.count}`}
                            >
                                {c.count}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {abierto && (
                <div
                    style={{ top: headerOffset + 64 }}
                    className="fixed right-4 z-50 w-[380px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-7rem)] bg-white dark:bg-gray-800 rounded-xl shadow-2xl flex flex-col overflow-hidden border border-teal-200 dark:border-teal-800"
                >
                    <div className="px-4 py-3 bg-teal-600 text-white">
                        <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm">Alertas de Obras</p>
                            <button type="button" onClick={() => setAbierto(false)} className="text-white/80 hover:text-white text-lg leading-none">✕</button>
                        </div>
                        <p className="text-xs text-white/80 mt-0.5">Anticipo pagado · falta acta de inicio</p>
                    </div>

                    <div className="px-4 pt-3 bg-gray-50 dark:bg-gray-900">
                        <input
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por código de obra (ej. 025-012)"
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-900">
                        {cargando && pendientes.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">Cargando…</div>
                        )}
                        {!cargando && filtradas.length === 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                                Sin coincidencias para «{busqueda.trim()}».
                            </div>
                        )}
                        {grupos.map(([ciclo, items]) => (
                            <div key={ciclo} className="space-y-2">
                                <div className="flex items-center justify-between px-1">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE_CICLO[items[0].color] || BADGE_CICLO.violeta}`}>
                                        Ciclo {ciclo}
                                    </span>
                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                        {items.length}
                                    </span>
                                </div>
                        {items.map((p) => {
                            const esPv = Number(p.ciclo) >= 3;
                            return (
                                <div
                                    key={`${p.id}-${p.ciclo}`}
                                    className={`rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800 space-y-2 ${BORDE_CICLO[p.color] || BORDE_CICLO.violeta}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => handleClickObra(p.codigoObra)}
                                                onDoubleClick={() => handleDobleClickObra(p.codigoObra)}
                                                title="Click para filtrar · doble click para abrir la obra"
                                                className="font-medium text-blue-700 dark:text-blue-400 hover:underline"
                                            >
                                                {p.codigoObra}
                                            </button>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                                {p.nombreObra || '—'}
                                            </p>
                                        </div>
                                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${BADGE_CICLO[p.color] || BADGE_CICLO.violeta}`}>
                                            Ciclo {p.ciclo}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Inicio {fechaDia(p.fechaBase)} · Vence {fechaDia(p.fechaVencimiento)}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-4">
                                        <span>Transcurridos: <span className="font-semibold">{transcurridos(p.diasTranscurridos)}</span></span>
                                        <span>Restantes: <span className="font-semibold">{restantes(p.diasRestantes)}</span></span>
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300">
                                        {p.hitoPendiente?.label || (esPv ? 'Confeccionar PV' : 'Notificación')}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => abrirAccion(p)}
                                        className="w-full px-3 py-1.5 rounded-md text-sm font-medium bg-teal-600 text-white hover:bg-teal-700"
                                    >
                                        {ETIQUETA_ACCION(p.ciclo)}
                                    </button>
                                </div>
                            );
                        })}
                            </div>
                        ))}
                    </div>

                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <button
                            type="button"
                            onClick={() => { setAbierto(false); navigate('/alertas-obras'); }}
                            className="w-full text-sm text-teal-700 dark:text-teal-400 hover:underline"
                        >
                            Ir a Alertas de Obras
                        </button>
                    </div>
                </div>
            )}

            <Modal
                isOpen={Boolean(accion)}
                onClose={cerrarAccion}
                title={accion ? `${ETIQUETA_ACCION(accion.item.ciclo)} · ${accion.item.codigoObra}` : ''}
                maxWidth="max-w-2xl"
            >
                {cargandoDraft && <p className="text-sm text-gray-500">Cargando el mail…</p>}
                {!cargandoDraft && draft && (
                    <div className="space-y-3">
                        {accion.esPv && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nro de PV <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={draft.nroPv}
                                    onChange={(e) => setDraft((p) => ({ ...p, nroPv: e.target.value }))}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    placeholder="N° PV"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Destinatarios</label>
                            <input
                                type="text"
                                value={draft.destinatarios}
                                onChange={(e) => setDraft((p) => ({ ...p, destinatarios: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                placeholder="mail@organizacion.org, otro@organizacion.org"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Separados por coma. Obligatorio al menos uno.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Asunto</label>
                            <input
                                type="text"
                                value={draft.asunto}
                                onChange={(e) => setDraft((p) => ({ ...p, asunto: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cuerpo</label>
                            <textarea
                                rows={9}
                                value={draft.cuerpo}
                                onChange={(e) => setDraft((p) => ({ ...p, cuerpo: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Observación interna (opcional)
                            </label>
                            <textarea
                                rows={2}
                                value={draft.descripcion}
                                onChange={(e) => setDraft((p) => ({ ...p, descripcion: e.target.value }))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                        </div>
                        {mensaje && <p className="text-sm text-red-600">{mensaje}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                onClick={cerrarAccion}
                                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={enviarAccion}
                                disabled={enviando}
                                className="px-3 py-1.5 text-sm rounded-md bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50"
                            >
                                {enviando
                                    ? 'Enviando…'
                                    : accion.esPv
                                        ? 'Enviar PV y cerrar ciclo'
                                        : 'Enviar notificación'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default AlertasObrasBurbuja;
